// Line items from GET /daysmart/clients/{id}/previous-appointments.
// The endpoint has returned snake_case since 2026. Older payloads used DaySmart PascalCase.
// Same-calendar-day lines are one visit, so a multi-pet stop keeps every pet and service.

const firstDefined = (...values) => {
    for (const value of values) {
        if (value !== undefined && value !== null && value !== '') return value;
    }
    return undefined;
};

const instantFrom = (value) => {
    if (value instanceof Date) {
        const time = value.getTime();
        return Number.isFinite(time) ? { raw: value.toISOString(), time } : null;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
        return { raw: new Date(value).toISOString(), time: value };
    }
    if (typeof value === 'string') {
        const raw = value.trim();
        if (!raw) return null;
        const time = new Date(raw).getTime();
        return Number.isFinite(time) ? { raw, time } : null;
    }
    return null;
};

const visitInstant = (service) => {
    const candidates = [
        service.start_time,
        service.StartTime,
        service.date_scheduled,
        service.DateScheduled,
    ];
    for (const candidate of candidates) {
        const instant = instantFrom(candidate);
        if (instant) return instant;
    }
    return null;
};

const textField = (...values) => {
    const value = firstDefined(...values);
    if (value === undefined) return '';
    return String(value).trim();
};

export const consolidatePreviousAppointments = (services, locationId, limit = 5) => {
    if (!Array.isArray(services) || services.length === 0) return [];

    const grouped = new Map();

    services.forEach((service, index) => {
        if (!service || typeof service !== 'object') return;

        const instant = visitInstant(service);
        const ticketId = firstDefined(service.ticket_id, service.TicketID, service.TicketId);
        const groupKey = instant
            ? new Date(instant.time).toDateString()
            : (ticketId !== undefined ? `ticket:${ticketId}` : `row:${index}`);

        if (!grouped.has(groupKey)) {
            grouped.set(groupKey, {
                date: instant ? new Date(instant.time).toDateString() : null,
                start_time: instant ? instant.raw : null,
                sortTime: instant ? instant.time : null,
                pet_names: [],
                employee_name: textField(service.employee_name, service.EmployeeName) || null,
                ticket_id: ticketId ?? null,
                location_id: locationId,
                services: [],
                duration: 0,
            });
        }

        const group = grouped.get(groupKey);
        if (instant && (group.sortTime === null || instant.time < group.sortTime)) {
            group.sortTime = instant.time;
            group.start_time = instant.raw;
            group.date = new Date(instant.time).toDateString();
        }

        const petName = textField(service.pet_name, service.PetName);
        if (petName && !group.pet_names.includes(petName)) {
            group.pet_names.push(petName);
        }

        const serviceName = textField(service.item_description, service.ItemDescription);
        if (serviceName) group.services.push(serviceName);

        const duration = Number(firstDefined(service.duration, service.Duration));
        if (Number.isFinite(duration)) group.duration += duration;

        if (!group.employee_name) {
            const employeeName = textField(service.employee_name, service.EmployeeName);
            if (employeeName) group.employee_name = employeeName;
        }
        if (group.ticket_id == null && ticketId != null) {
            group.ticket_id = ticketId;
        }
    });

    return Array.from(grouped.values())
        .map(({ sortTime, ...appointment }) => ({
            ...appointment,
            pet_name: appointment.pet_names.join(' & '),
        }))
        .sort((a, b) => {
            const aTime = a.start_time ? new Date(a.start_time).getTime() : NaN;
            const bTime = b.start_time ? new Date(b.start_time).getTime() : NaN;
            const aOk = Number.isFinite(aTime);
            const bOk = Number.isFinite(bTime);
            if (aOk && bOk) return bTime - aTime;
            if (aOk) return -1;
            if (bOk) return 1;
            return 0;
        })
        .slice(0, limit);
};
